import React from "react";

import { FreeFortuneContent } from "shared/types";
import ContentSeo from "./ContentSeo";
import { useFreeFortuneContentFormatDate } from "features/無料占いコンテンツ/hooks/useFreeFortuneContentFormatDate";
import Body from "../layout/Body";
import { BreadCrumb } from "../molecules/BreadCrumb";
import { Image } from "shared/components/atoms/Image";
import { CategoryLabel } from "../v2/CategoryLabel";
import { LastModLabel } from "../v2/LastModLabel";
import { ContentItem } from "../organisms/ContentItem";
import { RelatedContentsFortuneTeller } from "../organisms/RelatedContents/FortuneTeller";
import { AllLabel } from "../molecules/AllLabel";
import { RelatedContentsTagDivination } from "../organisms/RelatedContents/TagDivination";
import { RelatedContentsCategoryDivination } from "../organisms/RelatedContents/CategoryDivination";
import { FortuneTellerMenu } from "../organisms/RelatedContents/FortuneTellerMenu";
import { ScatteredCards } from "../organisms/ScatteredCards";
import { InnerResult } from "../organisms/InnerResult";
import { repository } from "repositories";
import useSWR from "swr";
import { useFreeFortuneContext } from "features/無料占いコンテンツ/hooks/useFreeFortuneContext";

const useFreeFortuneContentResultList = (contentId: number) => {
  const repo = repository.freeFortuneResultContent.getList(contentId);
  const { data } = useSWR(repo.path, repo.data);

  return {
    ...data,
  };
};

interface Props {
  content: FreeFortuneContent;
}

export const TarotContent = ({ content }: Props) => {
  const { freeFortuneResultContents } = useFreeFortuneContentResultList(
    content.id
  );

  const { lastmod } = useFreeFortuneContentFormatDate(content);
  const { modal } = useFreeFortuneContext();

  return (
    <>
      <ContentSeo freeFortuneContent={content} />
      <Body css={modal.current && { filter: "blur(6px)" }}>
        <article>
          <BreadCrumb freeFortuneContent={content} tw="p-2.5" />
          <Image
            src={content.imageUrl}
            width={376}
            height={191}
            layout="responsive"
            objectFit="cover"
            priority
            alt={`「${content.title}」のアイキャッチ画像`}
          />
          <section tw="px-4 mt-6 mb-10">
            <p tw="font-semibold text-xs leading-5 tracking-[.3] text-grape">
              無料タロット占い
            </p>
            <h1 tw="font-bold text-xl tracking-[.11px] text-black mt-0.5">
              {content.title}
            </h1>
            <div tw="flex items-center mt-5">
              <CategoryLabel
                category={content.freeFortuneCategory}
                linkable={true}
                tw="mr-2.5"
              />
              <LastModLabel>{lastmod}</LastModLabel>
            </div>
            <p tw="font-normal text-[11px] leading-[18px] tracking-[.06px] text-black mt-2.5">
              {content.description}
            </p>
          </section>
          <ScatteredCards />
          <ContentItem
            items={content.freeFortuneItems}
            fortuneTeller={content.fortuneTeller}
            freeFortuneContent={content}
            tw="mt-10"
          />
          <InnerResult
            freeFortuneResultContents={freeFortuneResultContents}
            tw="mt-16 mx-6"
          />
        </article>

        <RelatedContentsFortuneTeller fortuneTeller={content.fortuneTeller} />
        {content && <FortuneTellerMenu freeFortuneContent={content} />}
        <AllLabel tw="mt-10 mb-5" />
        {content.freeFortuneDivination &&
          content.primaryTags &&
          content.primaryTags[0] && (
            <RelatedContentsTagDivination
              divination={content.freeFortuneDivination}
              tag={content.primaryTags[0]}
              tw="pt-[30px]"
            />
          )}
        {content.freeFortuneDivination &&
          content.secondaryTags &&
          content.secondaryTags.map((tag) => (
            <RelatedContentsTagDivination
              key={tag.id}
              divination={content.freeFortuneDivination}
              tag={tag}
              tw="pt-[30px]"
            />
          ))}
        {content.freeFortuneCategory && content.freeFortuneDivination && (
          <RelatedContentsCategoryDivination
            category={content.freeFortuneCategory}
            divination={content.freeFortuneDivination}
            tw="pt-[30px]"
          />
        )}
      </Body>
    </>
  );
};
